import { Rocket, Zap, X, Check, Sparkles } from "lucide-react";
import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

/**
 * Shows two payment options when the backend returns a 402:
 *  - Full subscription (unlimited)
 *  - One-off pay-per-reveal (€5)
 * detail shape: { upgrade_needed, lookup_key, message, oneoff?: {lookup_key, amount_eur, target_type, target_id, label} }
 */
export default function PaywallModal({ detail, onClose }) {
  const [busy, setBusy] = useState(false);
  if (!detail) return null;

  const isWorkerPlan = detail.lookup_key === "worker_pro_monthly";
  const planPriceLabel = isWorkerPlan ? "€9 / month" : "€49 / month";
  const planTitle = isWorkerPlan ? "Worker Pro" : "Employer Pro";
  const planPerks = isWorkerPlan
    ? [
        "Unlimited job applications",
        "Reveal every employer contact",
        "Priority listing in Browse Workers",
        "Cancel anytime from your dashboard",
      ]
    : [
        "Contact every worker on the site",
        "Post unlimited jobs",
        "Who-viewed-my-job analytics",
        "Cancel anytime from your dashboard",
      ];

  const oneoff = detail.oneoff;

  const startCheckout = async (lookup_key, metadata) => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key,
        origin_url: window.location.origin,
        metadata: metadata || {},
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Checkout failed");
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="paywall-modal"
      className="fixed inset-0 z-50 grid place-items-center bg-neutral-950/85 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl bg-neutral-900 border border-neutral-800 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          data-testid="paywall-close"
          onClick={onClose}
          className="absolute top-4 right-4 text-neutral-400 hover:text-amber-500"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="p-8 border-b border-neutral-800">
          <div className="text-amber-500 text-[10px] font-bold tracking-[0.3em] uppercase mb-2">
            Unlock this contact
          </div>
          <h2 className="display text-3xl">Two ways to get through</h2>
          <p className="text-neutral-400 text-sm mt-2">{detail.message}</p>
        </div>

        <div className="grid md:grid-cols-2 gap-px bg-neutral-800">
          {/* Subscription option */}
          <div className="bg-neutral-900 p-6 flex flex-col">
            <div className="flex items-center gap-2">
              <Rocket className="w-5 h-5 text-amber-500" />
              <div className="text-[10px] uppercase tracking-widest text-neutral-500">Best value</div>
            </div>
            <div className="display text-2xl mt-2">{planTitle}</div>
            <div className="text-amber-500 display text-4xl mt-1">{planPriceLabel}</div>
            <ul className="mt-4 space-y-2 flex-1">
              {planPerks.map((p) => (
                <li key={p} className="flex items-start gap-2 text-sm text-neutral-200">
                  <Check className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                  {p}
                </li>
              ))}
            </ul>
            <button
              data-testid="paywall-subscribe-btn"
              onClick={() => startCheckout(detail.lookup_key)}
              disabled={busy}
              className="mt-6 w-full py-3 bg-amber-500 text-neutral-950 uppercase tracking-widest text-xs font-bold hover:bg-amber-400 transition-colors disabled:opacity-60"
            >
              {busy ? "Redirecting…" : `Subscribe · ${planPriceLabel}`}
            </button>
          </div>

          {/* One-off option */}
          {oneoff && (
            <div className="bg-neutral-900 p-6 flex flex-col">
              <div className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-neutral-400" />
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">Just this one</div>
              </div>
              <div className="display text-2xl mt-2">Pay per contact</div>
              <div className="text-neutral-100 display text-4xl mt-1">€{oneoff.amount_eur}</div>
              <ul className="mt-4 space-y-2 flex-1">
                <li className="flex items-start gap-2 text-sm text-neutral-200">
                  <Check className="w-4 h-4 text-neutral-400 mt-0.5 shrink-0" />
                  Reveal this one contact only
                </li>
                <li className="flex items-start gap-2 text-sm text-neutral-200">
                  <Check className="w-4 h-4 text-neutral-400 mt-0.5 shrink-0" />
                  No subscription, no monthly fee
                </li>
                <li className="flex items-start gap-2 text-sm text-neutral-200">
                  <Check className="w-4 h-4 text-neutral-400 mt-0.5 shrink-0" />
                  Instant access after payment
                </li>
              </ul>
              <button
                data-testid="paywall-oneoff-btn"
                onClick={() =>
                  startCheckout(oneoff.lookup_key, {
                    target_type: oneoff.target_type,
                    target_id: oneoff.target_id,
                  })
                }
                disabled={busy}
                className="mt-6 w-full py-3 border border-amber-500 text-amber-500 uppercase tracking-widest text-xs font-bold hover:bg-amber-500 hover:text-neutral-950 transition-colors disabled:opacity-60"
              >
                {busy ? "Redirecting…" : `Pay once · €${oneoff.amount_eur}`}
              </button>
            </div>
          )}
        </div>

        <div className="px-8 py-4 border-t border-neutral-800 text-center text-[10px] uppercase tracking-widest text-neutral-500 flex items-center justify-center gap-2">
          <Sparkles className="w-3 h-3 text-amber-500" />
          Secure checkout by Stripe · Payments in EUR
        </div>
      </div>
    </div>
  );
}
